import { Component, ChangeDetectionStrategy, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, map, tap, take } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';

import { ConfigService, RootPaths } from '@fcom/core';
import { BreadcrumbService, CmsPageService, LoaderType } from '@fcom/common';
import { CmsContentType, CmsPage, CmsImageData, CmsTemplate } from '@fcom/core-api';
import { getBackgroundImageForCmsTemplate } from '@fcom/cms/services/cms-utils/cms-utils';
import { BookingLocationCodes } from '@fcom/common/interfaces/booking';
import { unsubscribe } from '@fcom/core/utils';

import { selectData, selectSearch, selectStatus } from '../../store/selectors';
import { setSearch } from '../../store/actions/search.actions';
import { DataState, SearchState, TimetableFeatureState, TimetableStatus } from '../../interfaces/index';

@Component({
  selector: 'fin-timetable',
  templateUrl: './timetable.component.html',
  styleUrls: ['./timetable.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimetableComponent implements OnInit, OnDestroy {
  search$: Observable<SearchState>;
  locations$: Observable<BookingLocationCodes[]>;
  data$: Observable<DataState>;
  noSearchResults$: Observable<boolean>;
  showBookingWidget$: Observable<boolean>;
  hasInitialLoadCompleted = false;

  template$: Observable<CmsTemplate>;
  backgroundImage$: Observable<CmsImageData>;

  readonly LoaderType = LoaderType;
  private subscription: Subscription = new Subscription();

  enablePageFeedback = false;

  constructor(
    private store: Store<TimetableFeatureState>,
    private pageService: CmsPageService,
    private breadcrumbService: BreadcrumbService,
    private configService: ConfigService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.route.queryParams.pipe(take(1)).subscribe((params) => {
      if (!params?.origin || !params?.dest) {
        return;
      }
      this.store.dispatch(
        setSearch({
          origin: { code: params.origin },
          destination: { code: params.dest },
        })
      );
    });

    this.template$ = this.pageService.getPageData(RootPaths.TIMETABLE_ROOT).pipe(
      filter((template) => template.status !== 404),
      tap((template) => {
        const cmsPage = template?.header?.[0] as CmsPage;

        this.pageService.setMeta(
          template.header.find((obj) => obj.contentContentType === CmsContentType.CMChannel) as CmsPage
        );
        this.breadcrumbService.setBreadcrumbNormal(cmsPage.navigationPathList);
      })
    );

    this.backgroundImage$ = getBackgroundImageForCmsTemplate(this.template$);

    this.search$ = this.store.pipe(selectSearch());
    this.locations$ = this.search$.pipe(
      map((search) => {
        return [
          {
            originLocationCode: search.origin.code,
            destinationLocationCode: search.destination.code,
          },
        ];
      })
    );
    this.data$ = this.store.pipe(selectData());
    this.noSearchResults$ = this.data$.pipe(
      map((data) => {
        return data?.flights && Object.keys(data.flights).length === 0;
      })
    );

    this.showBookingWidget$ = this.store.pipe(selectStatus()).pipe(
      map((status) => status.status),
      map((status) => {
        if (this.hasInitialLoadCompleted) {
          return true;
        }

        if (status === TimetableStatus.READY || status === TimetableStatus.ERROR) {
          this.hasInitialLoadCompleted = true;

          return true;
        }

        return false;
      }),
      distinctUntilChanged()
    );

    if (this.configService.cfg.enableQualtricsPageFeedback) {
      this.enablePageFeedback = true;
    }
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }
}
